// react
import { useCallback, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ClearIcon from '@mui/icons-material/Clear';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

// material ui
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// feature-hooks
import useUrlAnalysisScope from '@/features/energyxplorer/hooks/useUrlAnalysisScope';
import useUrlSeu from '@/features/energyxplorer/hooks/useUrlSeu';

// components
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

// relative
import { useEnergyTreeNode } from '../../hooks/useEnergyTreeNode';

function StartTypeAvatar({ type }) {
  return (
    <Avatar
      variant="rounded"
      sx={{
        backgroundColor: getTypeColor(type) || '#555',
        borderRadius: 2,
        color: '#fff',
        fontWeight: 'bold',
        height: 32,
        width: 32,
      }}
    >
      <i
        style={{ fontSize: '16px' }}
        className={`fas ${getTypeIcon(type) || 'fa-circle'}`}
      ></i>
    </Avatar>
  );
}

function NodeLabel({ node, onSelect, t }) {
  const labelText = node.name ?? `Node ${node.energyTreeNodeId}`;
  const childCount = node.children?.length || 0;
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 0.5,
        pr: 0.5,
        minHeight: 36,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StartTypeAvatar type={node.type} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {labelText}
        </Typography>
        {childCount > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {childCount}{' '}
            {childCount === 1 ? t('common.child') : t('common.children')}
          </Typography>
        )}
      </Box>

      {isHovered && (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(node);
          }}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          {t('common.select_node')}
        </Button>
      )}
    </Stack>
  );
}

// Helper function to build hierarchical tree structure from flat array
function buildHierarchicalTree(flatNodes) {
  if (!flatNodes?.length) return [];

  // Create a map of all nodes by ID
  const nodeMap = new Map();
  flatNodes.forEach((node) => {
    nodeMap.set(node.energyTreeNodeId, {
      ...node,
      children: [],
    });
  });

  // Build the tree structure
  const rootNodes = [];
  flatNodes.forEach((node) => {
    const nodeWithChildren = nodeMap.get(node.energyTreeNodeId);
    if (node.parentId && nodeMap.has(node.parentId)) {
      // Add to parent's children
      nodeMap.get(node.parentId).children.push(nodeWithChildren);
    } else {
      // Root node (parentId is null)
      rootNodes.push(nodeWithChildren);
    }
  });

  return rootNodes;
}

function renderTree({ node, onSelect, t }) {
  return (
    <TreeItem
      key={node.energyTreeNodeId}
      itemId={String(node.energyTreeNodeId)}
      label={<NodeLabel node={node} onSelect={onSelect} t={t} />}
      sx={{
        '& .MuiTreeItem-content': {
          paddingY: 0,
        },
      }}
    >
      {Array.isArray(node.children) &&
        node.children.map((child) => renderTree({ node: child, onSelect, t }))}
    </TreeItem>
  );
}

const EnergyTreeNodeTraversal = ({
  showModeToggle = false,
  displayChildNodeList = true,
}) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const { energyTreeNode, setNodeId } = useEnergyTreeNode();

  const { energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const [searchValue, setSearchValue] = useState('');

  const [analysisScope, setAnalysisScope] = useUrlAnalysisScope({
    allowed: ['node', 'seu'],
    fallback: 'node',
  });

  // Build hierarchical tree structure
  const hierarchicalTree = useMemo(() => {
    const items = selectedEnergyGroup?.energyTreeNodes || [];
    return buildHierarchicalTree(items);
  }, [selectedEnergyGroup]);

  // Get default expanded items - first root node
  const defaultExpanded = useMemo(() => {
    if (hierarchicalTree.length > 0) {
      return [String(hierarchicalTree[0].energyTreeNodeId)];
    }
    return [];
  }, [hierarchicalTree]);

  // Build a searchable flat index once. Each entry keeps its parent path so
  // search can show context without rendering the full tree.
  const searchableNodes = useMemo(() => {
    const items = selectedEnergyGroup?.energyTreeNodes || [];
    const nodeById = new Map(
      items.map((node) => [node.energyTreeNodeId, node]),
    );

    return items.map((node) => {
      const path = [];
      const visited = new Set();
      let parentId = node.parentId;

      while (parentId && nodeById.has(parentId) && !visited.has(parentId)) {
        visited.add(parentId);
        const parent = nodeById.get(parentId);
        path.unshift(parent.name ?? `Node ${parent.energyTreeNodeId}`);
        parentId = parent.parentId;
      }

      return {
        node,
        path,
        searchText: `${node.name ?? ''} ${node.type ?? ''}`.toLowerCase(),
      };
    });
  }, [selectedEnergyGroup]);

  const searchResults = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return [];

    return searchableNodes.filter(({ searchText }) =>
      searchText.includes(query),
    );
  }, [searchValue, searchableNodes]);

  const [open, setOpen] = useState(false);

  const handleNodeSelect = (node) => {
    setNodeId(node.energyTreeNodeId);
    setOpen(false);
    setSearchValue(''); // Clear search when dialog closes
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue(''); // Clear search when dialog closes
  };

  const handleAnalysisScopeChange = useCallback(
    (nextMode) => {
      if (nextMode === analysisScope) return;
      setAnalysisScope(nextMode);
    },
    [analysisScope, setAnalysisScope],
  );

  // Node lookup
  const nodeLookup = useMemo(() => {
    const map = new Map();

    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      map.set(node.energyTreeNodeId, { ...node, children: [] });
    });

    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      if (node.parentId && map.has(node.parentId)) {
        map.get(node.parentId).children.push(node);
      }
    });

    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(
    () => nodeLookup.get(energyTreeNode?.energyTreeNodeId)?.children || [],
    [energyTreeNode, nodeLookup],
  );

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (!type) return;
      if (!map.has(type)) map.set(type, []);
      map.get(type).push(node);
    });
    return map;
  }, [selectedEnergyGroup]);

  const validSeuOptions = Array.from(nodeGroupsByType.entries())
    .filter(([, nodes]) =>
      nodes.some((node) => (node?.dataStreams?.length ?? 0) > 0),
    )
    .map(([type]) => type)
    .filter(
      (type) =>
        type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
    );

  const [seuToAnalyse, setSeuToAnalyse] = useUrlSeu({
    key: 'type',
    allowed: validSeuOptions,
    fallback: () => validSeuOptions[0] ?? null,
  });

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={
            analysisScope === 'node'
              ? t('common.select_node') || 'Analyserer node'
              : t('common.select_seu') || 'Analyserer SEU'
          }
          sx={{ pb: 0 }}
          slotProps={{
            title: {
              variant: 'subtitle1',
              fontWeight: 600,
            },
          }}
        />
        <CardContent>
          <Stack>
            {showModeToggle && (
              <ToggleButtonGroup
                value={analysisScope}
                exclusive
                onChange={(event, newScope) =>
                  newScope && handleAnalysisScopeChange(newScope)
                }
                fullWidth
                sx={{ mb: 2 }}
              >
                <Tooltip
                  title={t('common.toggle_mode_node') || 'Node Analysis'}
                  placement="top"
                >
                  <ToggleButton value="node">
                    <Typography variant="body2" fontWeight={500}>
                      {t('common.analyze_node') || 'Node Analysis'}
                    </Typography>
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={t('common.toggle_mode_seu') || 'SEU Analysis'}
                  placement="top"
                >
                  <span>
                    <ToggleButton
                      value="seu"
                      disabled={validSeuOptions.length === 0}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {t('common.analyze_seu') || 'SEU Analysis'}
                      </Typography>
                    </ToggleButton>
                  </span>
                </Tooltip>
              </ToggleButtonGroup>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                fullWidth
                startIcon={
                  analysisScope === 'node' && energyTreeNode ? (
                    <StartTypeAvatar type={energyTreeNode.type} />
                  ) : (
                    <StartTypeAvatar type={seuToAnalyse} />
                  )
                }
                endIcon={<UnfoldMoreRoundedIcon />}
              >
                <Box
                  sx={{
                    flex: 1,
                    textAlign: 'left',
                    overflow: 'hidden',
                  }}
                >
                  {analysisScope === 'node' ? (
                    energyTreeNode ? (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                          title={energyTreeNode.name}
                        >
                          {energyTreeNode.name}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2">
                        {t('common.select_node') || 'Vælg node'}
                      </Typography>
                    )
                  ) : (
                    <Typography>
                      {seuToAnalyse || t('common.select_seu')}
                    </Typography>
                  )}
                </Box>
              </Button>
              <IconButton
                onClick={() =>
                  energyTreeNode.parentId && setNodeId(energyTreeNode.parentId)
                }
                disabled={!energyTreeNode.parentId || analysisScope === 'seu'}
                sx={{
                  p: 1.2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '6px',
                }}
              >
                <ArrowUpwardRoundedIcon />
              </IconButton>
            </Box>
            {analysisScope === 'node' && displayChildNodeList && (
              <List dense sx={{ maxHeight: 425, overflowY: 'auto' }}>
                {children.length > 0 ? (
                  children.map((child) => (
                    <ListItemButton
                      key={child.energyTreeNodeId}
                      onClick={() => setNodeId(child.energyTreeNodeId)}
                      sx={{ borderRadius: 2 }}
                    >
                      <ListItemIcon>
                        <StartTypeAvatar type={child.type} />
                      </ListItemIcon>
                      <ListItemText
                        primary={child.name}
                        secondary={child.type}
                        slotProps={{
                          primary: {
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          },
                        }}
                      />
                    </ListItemButton>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, textAlign: 'center', color: 'gray' }}
                  >
                    {t('common.no_further_breakdown')}
                  </Typography>
                )}
              </List>
            )}
          </Stack>
        </CardContent>
      </Card>
      <Dialog maxWidth="xl" open={open} onClose={handleClose}>
        <DialogTitle>
          {analysisScope === 'node'
            ? t('common.analyze_node')
            : t('common.analyze_seu')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ minWidth: 600 }}>
            {analysisScope === 'node' ? (
              <>
                <TextField
                  fullWidth
                  placeholder={t('actions.searchItem', {
                    ns: 'common',
                    item: t('common.select_node'),
                  })}
                  variant="outlined"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: {
                      endAdornment: searchValue ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchValue('')}
                            edge="end"
                            aria-label={t('actions.reset', { ns: 'common' })}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
                {searchValue.trim() ? (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        color: 'text.secondary',
                      }}
                    >
                      {searchResults.length}{' '}
                      {t('common.results', { count: searchResults.length })}
                    </Typography>

                    <List
                      dense
                      disablePadding
                      sx={{ maxHeight: 500, overflowY: 'auto' }}
                    >
                      {searchResults.map(({ node, path }) => (
                        <ListItemButton
                          key={node.energyTreeNodeId}
                          onClick={() => handleNodeSelect(node)}
                          selected={
                            energyTreeNode?.energyTreeNodeId ===
                            node.energyTreeNodeId
                          }
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            alignItems: 'flex-start',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 44, mt: 0.25 }}>
                            <StartTypeAvatar type={node.type} />
                          </ListItemIcon>

                          <ListItemText
                            primary={
                              node.name ?? `Node ${node.energyTreeNodeId}`
                            }
                            secondary={
                              <>
                                {path.length > 0 && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    {path.join(' › ')}
                                  </Typography>
                                )}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {node.type}
                                </Typography>
                              </>
                            }
                            slotProps={{
                              primary: {
                                fontWeight: 500,
                              },
                            }}
                          />
                        </ListItemButton>
                      ))}

                      {searchResults.length === 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            py: 4,
                            textAlign: 'center',
                            color: 'text.secondary',
                          }}
                        >
                          {t('common.no_results_found')}
                        </Typography>
                      )}
                    </List>
                  </Box>
                ) : (
                  <SimpleTreeView
                    aria-label="energy-tree-node-traversal"
                    defaultExpandedItems={defaultExpanded}
                  >
                    {hierarchicalTree.map((rootNode) =>
                      renderTree({
                        node: rootNode,
                        onSelect: handleNodeSelect,
                        t,
                      }),
                    )}
                  </SimpleTreeView>
                )}
              </>
            ) : (
              <List sx={{ height: 400, overflowY: 'auto' }}>
                {validSeuOptions.map((seuType) => (
                  <ListItemButton
                    key={seuType}
                    onClick={() => {
                      setSeuToAnalyse(seuType);
                      setOpen(false);
                    }}
                    selected={seuToAnalyse === seuType}
                    sx={{ borderRadius: 1, mb: 1 }}
                  >
                    <ListItemIcon>
                      <StartTypeAvatar type={seuType} />
                    </ListItemIcon>
                    <ListItemText
                      primary={seuType}
                      secondary={`${nodeGroupsByType.get(seuType)?.length || 0} noder`}
                    />
                  </ListItemButton>
                ))}
                {validSeuOptions.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}
                  >
                    {t('nodes.traversal.no_seu_options', {
                      ns: 'energyxplorer',
                    })}
                  </Typography>
                )}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnergyTreeNodeTraversal;
